/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import AdminTablePlaceholder from 'src/components/Admin/AdminTablePlaceholder';
import AdminTradeRuleList from 'src/components/Admin/AdminTradeRule/AdminTradeRuleList';
import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';
import { DEFAULT_LIST_PAGE_SIZE } from 'src/util/constants';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

const messages = defineMessages({
  initialLoadError: {
    id: 'admin.admin_trade_rule.admin_trade_rule_list.page.initial_load_error.message',
    defaultMessage: 'An error occurred while loading trade rules. Please try again.',
  },
});

export const adminTradeRuleListRouteQuery = graphql`
  query AdminTradeRuleListRoute_Query(
    $count: Int
    $cursor: String
    $state: TradeRuleState
    $type: TradeType
    $start: Timestamp
    $finish: Timestamp
  ) {
    viewer {
      ...AdminTradeRuleList_viewer @arguments(
        count: $count
        cursor: $cursor
        state: $state
        type: $type
        start: $start
        finish: $finish
      )
    }
  }
`;

/**
 * Retries with the route's current variables: the ones found-relay computed in
 * `prepareVariables` (`src/routes/index.jsx`), updated by every successful filter
 * or date refetch, so Retry reloads the filters shown in the URL. The list needs
 * no uuid, so Retry is always enabled.
 * @param {object} props - Route props.
 * @param {object} [props.variables] - Route query variables.
 * @returns {object} Retry variables.
 */
function getAdminTradeRuleListRetryVariables(props) {
  return { count: DEFAULT_LIST_PAGE_SIZE, ...props?.variables };
}

/**
 * Renders `AdminTradeRuleList` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @returns {React.ReactNode} admin trade rule list route content.
 */
function AdminTradeRuleListPreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(adminTradeRuleListRouteQuery, queryRef);

  return <AdminTradeRuleList {...routeProps} {...queryData} />;
}

AdminTradeRuleListPreloadedQueryRenderer.propTypes = {
  routeProps: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
};

/**
 * Hook-safe renderer for the `AdminTradeRuleList` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state. Nested inside the
 * `AdminLayout` boundary, which covers admin-shell errors.
 * Caller MUST mount this with `key={getRouteRendererKey(match, variables)}` so navigating
 * to another filter URL drops a retried query reference and applied filters.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} admin trade rule list route content.
 */
function AdminTradeRuleListRouteRenderer({ routeProps: initialRouteProps, routeError = null }) {
  const intl = useIntl();
  // Filter changes sync the URL with `doNotRerunMatch`, so found-relay never
  // recomputes the route variables; track the applied ones here instead.
  const [variables, setVariables] = useState(initialRouteProps.variables);
  const filtersAppliedFunc = useCallback((refetchVariables) => {
    setVariables((current) => ({ ...current, ...refetchVariables }));
  }, []);
  const routeProps = { ...initialRouteProps, variables, filtersAppliedFunc };
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    adminTradeRuleListRouteQuery,
    routeProps,
    {
      logLabel: 'AdminTradeRuleListRoute',
      getRetryVariablesFunc: getAdminTradeRuleListRetryVariables,
    },
  );

  return (
    <PlaceholderErrorBoundary
      bodyText={intl.formatMessage(messages.initialLoadError)}
      placeholderComponent={AdminTablePlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <AdminTradeRuleListPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <AdminTradeRuleList {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

AdminTradeRuleListRouteRenderer.propTypes = {
  routeProps: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Builds the renderer key from the route variables and the matched location.
 * found-relay calls the route with a fresh location on every navigation, even
 * back to the same filter URL, which remounts the renderer and drops filters
 * applied in the page and any retried query reference. The page's own
 * `doNotRerunMatch` URL sync does not rematch, so it leaves the key unchanged.
 * @param {object} [match] - Found route match.
 * @param {object} [variables] - Route query variables from `prepareVariables`.
 * @returns {string} A stable key for the route renderer.
 */
function getRouteRendererKey(match, variables) {
  const variablesKey = JSON.stringify(
    Object.entries(variables || {}).sort(([a], [b]) => a.localeCompare(b)),
  );
  return `${match?.location?.key ?? ''}|${variablesKey}`;
}

/**
 * Renders the AdminTradeRuleList route state supplied by found-relay.
 * A network error is forwarded to the renderer's `RouteErrorThrower` bridge so
 * the local error boundary can catch it. Authentication is resolved by the
 * owning `AdminLayout` route, not here.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @param {object} readyState.variables - Route query variables from `prepareVariables`.
 * @returns {React.ReactNode} AdminTradeRuleList route content or loading state.
 */
export function adminTradeRuleListRoute({
  error = null, match, props = null, variables,
}) {
  if (error) {
    const routeProps = {
      match, router: match.router, ...props, variables,
    };
    return (
      <AdminTradeRuleListRouteRenderer
        key={getRouteRendererKey(match, variables)}
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return (
    <AdminTradeRuleListRouteRenderer
      key={getRouteRendererKey(match, variables)}
      routeProps={{ ...props, variables }}
    />
  );
}

export default AdminTradeRuleListRouteRenderer;
